import { Button, Dialog, DialogContent, DialogTitle, DialogContentText, DialogActions, Typography, Box, InputAdornment } from '@mui/material';
import React, { useState, useEffect } from 'react';
import CustomDialogActions from '../form/theme-elements/CustomDialogActions';
import DialogContentComponent from './DialogContentComponent';
import CustomFormLabel from '../form/theme-elements/CustomFormLabel';
import { CustomOutlinedInput } from '../shared/CustomOutlinedInput';
import { IconMessage2 } from '@tabler/icons';
import dayjs from 'dayjs';

const MAX_CHARACTERS = 100;

const BookingCancelConfirmationDialog = ({ open, handleClose, handleRedirection, handleConfirmCancel, success, error, loading, cancellationResults }) => {
    const [cancellationNote, setCancellationNote] = useState('');
    const [noteError, setNoteError] = useState('');
    const [touched, setTouched] = useState(false);

    // Calculate the prefix that will be added to the note
    const currentDateTime = dayjs().format('M/D/YYYY, h:mm A');
    const prefix = `${currentDateTime}:`;
    const availableChars = MAX_CHARACTERS - prefix.length;

    // Reset cancellation note when dialog opens
    useEffect(() => {
        if (open && !loading && !success && !error) {
            setCancellationNote('');
            setNoteError('');
            setTouched(false);
        }
    }, [open, loading, success, error]);

    const validateNote = (value) => {
        if (!value.trim()) {
            return 'This field is required';
        }
        if (value.length > availableChars) {
            return `Maximum ${availableChars} characters allowed for the reason`;
        }
        return '';
    };

    const handleConfirmClick = () => {
        setTouched(true);
        const error = validateNote(cancellationNote);
        if (error) {
            setNoteError(error);
            return;
        }
        setNoteError('');

        // Combine prefix with user's note
        const formattedNote = `${prefix}${cancellationNote.trim()}`;
        handleConfirmCancel(formattedNote);
    };

    const handleNoteChange = (e) => {
        const value = e.target.value;

        // Limit input to available characters
        if (value.length <= availableChars) {
            setCancellationNote(value);
        }

        if (touched) {
            setNoteError(validateNote(value));
        }
    };

    const handleBlur = () => {
        setTouched(true);
        setNoteError(validateNote(cancellationNote));
    };

    const isNoteValid = cancellationNote.trim().length > 0 && cancellationNote.length <= availableChars;
    const remainingChars = availableChars - cancellationNote.length;

    if (!loading && !success && !error) {
        return (
            <Dialog
                open={open}
                onClose={handleClose}
                aria-labelledby="cancel-dialog-title"
                aria-describedby="cancel-dialog-description"
                fullWidth
            >
                <DialogTitle id="cancel-dialog-title">
                    Confirm Cancellation
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="cancel-dialog-description">
                        Are you sure you want to cancel this Reservation?
                        <Box size={12}>
                            <CustomFormLabel htmlFor="bi-message">
                                Cancellation Note *
                            </CustomFormLabel>
                        </Box>
                        <Box size={12}>
                            <CustomOutlinedInput
                                id="bi-message"
                                value={cancellationNote}
                                onChange={handleNoteChange}
                                onBlur={handleBlur}
                                startAdornment={
                                    <InputAdornment position="start">
                                        <IconMessage2 size="20" />
                                    </InputAdornment>
                                }
                                placeholder={`${prefix}Type your reason here...`}
                                multiline
                                fullWidth
                                error={!!noteError}
                                inputProps={{
                                    maxLength: availableChars
                                }}
                            />
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                mt: 0.5
                            }}>
                                <Typography
                                    variant="caption"
                                    color={remainingChars < 0 ? 'error' : (remainingChars < 20 ? 'warning.main' : 'text.secondary')}
                                    sx={{ ml: 'auto', minWidth: 60, textAlign: 'right' }}
                                >
                                    {cancellationNote.length}/{availableChars}
                                </Typography>
                            </Box>
                            {noteError && (
                                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                                    {noteError}
                                </Typography>
                            )}
                        </Box>
                        <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                            This action cannot be undone.
                        </Typography>
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} color="primary">
                        No, Keep Booking
                    </Button>
                    <Button
                        onClick={handleConfirmClick}
                        color="error"
                        variant="contained"
                        disabled={!isNoteValid}
                    >
                        Yes, Cancel Booking
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }

    return (
        <Dialog
            open={open}
            onClose={(event, reason) => {
                if (reason !== "backdropClick") handleClose();
            }}
            aria-labelledby="reservation-dialog-title"
            aria-describedby="reservation-dialog-description"
            fullWidth
        >
            <DialogTitle id="reservation-dialog-title">
                {success ? "Reservation Cancelled Successfully!" : error ? "Reservation Cancellation Failed" : "Processing Reservation Cancellation"}
            </DialogTitle>
            <DialogContent id="reservation-dialog-description">
                <DialogContentComponent
                    loading={loading}
                    error={error}
                    handleClose={handleClose}
                    title={success ? "Success" : ""}
                    message={success ? `Your ${cancellationResults.Message}` : ""}
                />
            </DialogContent>
            {!loading && (
                <CustomDialogActions>
                    <Button color="error" onClick={handleClose} disabled={success}>
                        Close
                    </Button>
                    {!error && (
                        <Button onClick={handleRedirection} autoFocus>
                            View Reservation Overview
                        </Button>
                    )}
                </CustomDialogActions>
            )}
        </Dialog>
    );
};

export default BookingCancelConfirmationDialog;